import React, { useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Tooltip
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectEndorsmentFormData,
  updateEndorsementFormData
} from 'src/features/rfqDataSlice';
import {
  customComponentMap,
  customComponentSettings
} from './employeeListCustomComponents.js';
import moment from 'moment';

function EmployeeListItemHorizontalEditable({ sumInsuredData, groupDetails, productId = '', saveRow = () => { }, listItems }) {
  const dispatch = useDispatch();
  const endorsementFormData = useSelector(selectEndorsmentFormData);

  const sectionId = sumInsuredData?.id;
  const groupId = groupDetails?.id;

  const tableListDatas = endorsementFormData?.sumInsuredSectionData?.[sectionId]?.groups?.[groupId]?.sumInsuredEntry || [];
  const columns = sumInsuredData.sumInsuredHeaderSorted || [];
  const renderValue = (attr, value) => {
    if (!value) return 'N/A';
    if (['dob', 'dateOfCoverage', 'dobOfMamber'].includes(attr)) {
      return moment(value).format('DD-MM-YYYY');
    }
    return Array.isArray(value) ? value.join(', ') : value;
  };
  const createdRows = tableListDatas.filter((r) => r.actionType === 'create');
  const deletedRows = tableListDatas.filter((r) => r.actionType === 'delete');

  const [cellEditMap, setCellEditMap] = React.useState({});
  const [cellInputMap, setCellInputMap] = React.useState({});
  const [cellStaticMap, setCellStaticMap] = React.useState({});

  const handleChange = (attr, rowIndex, value) => {
    const actualValue = value?.target?.value ?? value;
    const updatedEntryList = [...tableListDatas];
    updatedEntryList[rowIndex] = {
      ...updatedEntryList[rowIndex],
      [attr]: actualValue,
      savedStatus: false,
      actionType: (updatedEntryList[rowIndex].actionType == null || updatedEntryList[rowIndex].actionType == '') ? 'update' : updatedEntryList[rowIndex].actionType
    };

    const updatedGroup = {
      ...endorsementFormData.sumInsuredSectionData[sectionId].groups[groupId],
      sumInsuredEntry: updatedEntryList
    };

    const updatedSection = {
      ...endorsementFormData.sumInsuredSectionData[sectionId],
      groups: {
        ...endorsementFormData.sumInsuredSectionData[sectionId].groups,
        [groupId]: updatedGroup
      }
    };

    dispatch(
      updateEndorsementFormData({
        sumInsuredSectionData: {
          ...endorsementFormData.sumInsuredSectionData,
          [sectionId]: updatedSection
        }
      })
    );
  };

  const formatValue = (val: unknown): string => {
    if (!val) return 'N/A';

    // If it's already a Date
    if (val instanceof Date) {
      const day = String(val.getDate()).padStart(2, '0');
      const month = String(val.getMonth() + 1).padStart(2, '0');
      const year = val.getFullYear();
      return `${day}-${month}-${year}`;
    }

    // If it's a YYYY-MM-DD or ISO string
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
      const date = new Date(val);  // Works for both "2007-10-30" and "2007-10-30T00:00:00Z"
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      }
    }

    // If it's an array
    if (Array.isArray(val)) {
      return val.join(', ');
    }

    // Fallback
    return String(val);
  };

  const shouldShowHeads = tableListDatas?.some(
    (row) => row.actionType === "update" || row.actionType === null
  );



  const renderEditableCell = (attr, rowIndex, head, cellKey) => {
    const inputValue = cellInputMap[cellKey] || '';

    if (['dob', 'dateOfCoverage', 'dobOfMamber'].includes(attr)) {
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          import moment from "moment";

          <DatePicker
            views={['year', 'month', 'day']}
            inputFormat="dd-MM-yyyy"
            value={inputValue ? moment(inputValue, "DD-MM-YYYY").toDate() : null}
            onChange={(val) => {
              const formattedDate = val ? moment(val).format("DD-MM-YYYY") : null;

              setCellInputMap({
                ...cellInputMap,
                [cellKey]: formattedDate
              });

              handleChange(attr, rowIndex, formattedDate);
            }}
            maxDate={new Date()}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                fullWidth
                style={{ minWidth: "180px" }}
              />
            )}
          />
        </LocalizationProvider>
      );
    }

    if (attr === 'gender') {
      return (
        <FormControl fullWidth size="small" style={{ minWidth: '180px' }} >
          <InputLabel>{head.title || head.sumInsuredHeader.title}</InputLabel>
          <Select
            style={{ minWidth: '180px' }}
            label="Gender"
            value={inputValue || ''}
            onChange={(e) => {
              const newVal = e.target.value;
              setCellInputMap({
                ...cellInputMap,
                [cellKey]: newVal
              });
              handleChange(attr, rowIndex, e);
            }}
          >
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (attr === 'relationshipForTailormade') {
      return (
        <FormControl fullWidth size="small">
          <InputLabel>{head.title || head.sumInsuredHeader.title}</InputLabel>
          <Select
            style={{ minWidth: '180px' }}
            label="relationshipForTailormade"
            value={inputValue || ''}
            onChange={(e) => {
              const newVal = e.target.value;
              setCellInputMap({
                ...cellInputMap,
                [cellKey]: newVal
              });
              handleChange(attr, rowIndex, e);
            }}
          >
            <MenuItem value={'Self'}>Self</MenuItem>
            <MenuItem value={'Spouse'}>Spouse</MenuItem>
            <MenuItem value={'Child 1'}>Child 1</MenuItem>
            <MenuItem value={'Child 2'}>Child 2 </MenuItem>
            <MenuItem value={'Child 3'}>Child 3 </MenuItem>
            <MenuItem value={'Child 4'}>Child 4 </MenuItem>
            <MenuItem value={'Child 5'}>Child 5 </MenuItem>
            <MenuItem value={'Child 6'}>Child 6 </MenuItem>
            <MenuItem value={'Child 7'}>Child 7 </MenuItem>
            <MenuItem value={'Father'}>Father</MenuItem>
            <MenuItem value={'Mother'}>Mother</MenuItem>
            <MenuItem value={'Father-in-law'}>Father-in-law</MenuItem>
            <MenuItem value={'Mother-in-law'}>Mother-in-law</MenuItem>
            <MenuItem value={'Brother'}>Brother</MenuItem>
            <MenuItem value={'Sister'}>Sister</MenuItem>
          </Select>
        </FormControl>
      );
    }

    const CustomComponent = customComponentMap[attr];
    const CustomSetting = customComponentSettings[attr];
    if (CustomComponent) {
      return (
        <CustomComponent
          tableListDatas={tableListDatas}
          index={rowIndex}
          productId={productId}
          saveRow={saveRow}
          label={head.title || head.sumInsuredHeader.title || attr}
          apiUrl={CustomSetting}
          handleChange={handleChange}
        />
      );
    }

    return (
      <TextField
        style={{ minWidth: '180px' }}
        size="small"
        fullWidth
        type={
          ['age', 'sumInsured', 'premium', 'topupSI'].includes(attr)
            ? 'number'
            : 'text'
        }
        value={inputValue}
        onChange={(e) => {
          const newVal = e.target.value;
          setCellInputMap({
            ...cellInputMap,
            [cellKey]: newVal
          });
        }}
        onBlur={() => {
          handleChange(attr, rowIndex, inputValue);
        }}
      />
    );
  };

  return (
    <>
      <TableContainer sx={{ mt: shouldShowHeads ? 1 : 0 }} component={Paper}>
        <Table size="small">
          {shouldShowHeads && (<TableHead>
            <TableRow>
              <TableCell>SR No</TableCell>
              {sumInsuredData.sumInsuredHeaderSorted.map((head) => {
                const attr = head.sumInsuredHeader.attribute;
                return (
                  <TableCell key={attr}>
                    {head.title || head.sumInsuredHeader.title}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>)}
          <TableBody>
            {tableListDatas?.map((row, rowIndex) => {
              const isHidden =
                row.actionType === "delete" || row.actionType === "create";

              if (isHidden) return null;
              return (
                <TableRow key={rowIndex}>
                  <TableCell>{rowIndex + 1}</TableCell>
                  {sumInsuredData.sumInsuredHeaderSorted.map((head) => {
                    const attr = head.sumInsuredHeader.attribute;
                    const value = row[attr];
                    const cellKey = `${rowIndex}_${attr}`;
                    const isCellEditable = cellEditMap[cellKey] || false;

                    const isEditableAllowed =
                      endorsementFormData?.type === 'Both' ||
                      (endorsementFormData?.type === 'Financial' &&
                        head?.endorsementSubTypes?.financial?.includes(endorsementFormData?.subType)) ||
                      (endorsementFormData?.type === 'Non-Financial' &&
                        head?.endorsementSubTypes?.nonFinancial?.includes(endorsementFormData?.subType));

                    return (
                      <TableCell key={cellKey}>
                        {!isEditableAllowed ? (
                          <Tooltip title="Editing disabled due to subtype restriction">
                            <span>
                              <Checkbox
                                checked={isCellEditable}
                                disabled
                              />
                            </span>
                          </Tooltip>
                        ) : (
                          <Checkbox
                            checked={isCellEditable}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setCellEditMap({
                                ...cellEditMap,
                                [cellKey]: checked
                              });
                              if (checked) {
                                setCellStaticMap({
                                  ...cellStaticMap,
                                  [cellKey]: value
                                });
                                setCellInputMap({
                                  ...cellInputMap,
                                  [cellKey]: value || ''
                                });
                              }
                            }}
                          />
                        )}

                        <Typography display="inline" sx={{ mr: 1 }}>
                          {cellStaticMap[cellKey] !== undefined
                            ? formatValue(cellStaticMap[cellKey])
                            : formatValue(value)}
                        </Typography>
                        {isCellEditable && (
                          <div style={{ marginTop: 4 }}>
                            {renderEditableCell(attr, rowIndex, head, cellKey)}
                          </div>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>);
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {createdRows.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mt: 1 }}>Newly Added Entries</Typography>
          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>SR NO</TableCell>
                  {columns.map((col) => (
                    <TableCell key={col.sumInsuredHeader.attribute}>
                      {col.title || col.sumInsuredHeader.title}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {createdRows.map((row, index) => (
                  <TableRow key={row.id} style={{ backgroundColor: '#eaffea' }}>
                    <TableCell>{index + 1}</TableCell>
                    {columns.map((col) => {
                      const attr = col.sumInsuredHeader.attribute;
                      return <TableCell key={attr}>{renderValue(attr, row[attr])}</TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {deletedRows.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mt: 1 }}>Deleted Entries</Typography>
          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>SR NO</TableCell>
                  {columns.map((col) => (
                    <TableCell key={col.sumInsuredHeader.attribute}>
                      {col.title || col.sumInsuredHeader.title}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {deletedRows.map((row, index) => (
                  <TableRow key={row.id} style={{ backgroundColor: '#fdeaea' }}>
                    <TableCell>{index + 1}</TableCell>
                    {columns.map((col) => {
                      const attr = col.sumInsuredHeader.attribute;
                      return <TableCell key={attr}>{renderValue(attr, row[attr])}</TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </>
  );
}

export default EmployeeListItemHorizontalEditable;
